import { faPen, faTrash, faPlus, faUserGear } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import DataTable, { TableColumn } from "react-data-table-component";
import AddUserModal from "./AddUserModal";
import EditUserModal from "./EditUserModal";
import DeleteUserModal from "./DeleteUserModal";
import { UserData } from "src/interfaces/ManageUsersAndRole.interface";
import { useAppDispatch, useAppSelector } from "src/redux/storeHooks";
import { fetchUsersAsync, getUserStaffList } from "src/redux/actions/manageUserActions";
import useAppToast from "src/hooks/useAppToast";
import { loggedInAdminAccessToken } from "src/utils/constants";
import { useLocation, useNavigate } from "react-router-dom";
import { usePermission } from "src/hooks/usePermission";
import TableLoadingShell from "src/utils/TableLoadingShell";

const SEARCH_DEBOUNCE_MS = 300;

type AddManageUserFunctionalityProps = {
  setActiveView?: (view: "users" | "roles") => void;
};

const AddManageUserFunctionality = (props: AddManageUserFunctionalityProps) => {
  const isLoginAdminAcess = !(localStorage.getItem(loggedInAdminAccessToken));
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  console.log("Current location:", location.pathname);
  const isAccountLocation = location.pathname === "/account/settings" || location.pathname === "/settings-old";
  const { isRead, isWrite, isDelete } = usePermission();
  const { fetchedUserAccountData } = useAppSelector((state) => state.userAccountData);
  const { errorToast } = useAppToast();
  const errorToastRef = useRef(errorToast);
  const [userList, setUserList] = useState<UserData[]>([]);
  const [selectedUser, setSelectedUser] = useState<UserData | undefined>();
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [isFetching, setIsFetching] = useState(false);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const isSuperAdmin = fetchedUserAccountData?.is_superuser || fetchedUserAccountData?.account_type === 'ADMIN_STAFF';
  const userAccessLevel = fetchedUserAccountData?.role?.user_access?.toUpperCase();
  const canManageUsers = isSuperAdmin || userAccessLevel === "WRITE" || true;
  const canViewUsers = isSuperAdmin || userAccessLevel === "READ" || userAccessLevel === "WRITE";

  useEffect(() => { errorToastRef.current = errorToast; }, [errorToast]);

  const fetchUserData = useCallback(() => {
    setIsFetching(true);
    if (isSuperAdmin) {
      dispatch(
        fetchUsersAsync({
          paginationdata: {
            page_num: currentPage,
            page_limit: perPage,
            search: debouncedSearch,
          },
        })
      )
        .then((response) => {
          setUserList(response?.payload?.data?.data || []);
          setTotalRows(response?.payload?.data?.total || 0);
        })
        .catch((error) => {
          errorToastRef.current?.(error);
        })
        .finally(() => setIsFetching(false));
    }
    else {
      dispatch(
        getUserStaffList({
          paginationdata: {
            page_num: currentPage,
            page_limit: perPage,
            search: debouncedSearch,
          },
        })
      )
        .then((response) => {
          setUserList(response?.payload?.data?.data || []);
          setTotalRows(response?.payload?.data?.total || 0);
        })
        .catch((error) => {
          errorToastRef.current?.(error);
        })
        .finally(() => setIsFetching(false));
    }
  }, [currentPage, debouncedSearch, dispatch, perPage]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
      setCurrentPage(1);
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [searchTerm]);

  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  const clickManageRole = () => {
    // navigate("/manage-roles")
    props.setActiveView && props.setActiveView("roles");
  }

  const columns: TableColumn<UserData>[] = useMemo(() => {
    const baseColumns: TableColumn<UserData>[] = [
      { name: "User Name", selector: (row: UserData) => row.name, sortable: true, minWidth: "120px" },
      { name: "Email", selector: (row: UserData) => row.email, sortable: true, minWidth: "220px" },
      ...(isSuperAdmin
        ? [{
          name: "Role", selector: (row: UserData) => row.user_type, sortable: true, minWidth: "120px",
        }]
        : []), {
        name: "Status",
        minWidth: "120px",
        cell: (row: UserData) => (
          <span className={`badge text-capitalize px-3 py-2 rounded-pill bg-${row.status ? "success" : "secondary"}`}>
            {row.status ? "Active" : "Inactive"}
          </span>
        ),
      },
      {
        name: "Created Date",
        selector: (row: UserData) =>
          row.sign_up ? new Date(row.sign_up).toLocaleDateString() : "",
        minWidth: "130px",
      }
    ];
    if (isSuperAdmin) {
      baseColumns.push({
        name: "Last Login",
        selector: (row: UserData) =>
          row.last_login ? new Date(row.last_login).toLocaleDateString() : "",
        minWidth: "130px",
      });
    }

    if (canManageUsers) {
      const actionColumn: TableColumn<UserData> = {
        name: "Actions",
        cell: (row: UserData) => (
          <div className="d-flex gap-3 align-items-center">
            {isRead && <FontAwesomeIcon
              icon={faPen}
              size="lg"
              className="text-primary"
              title="Edit"
              style={{ cursor: "pointer" }}
              onClick={() => {
                setSelectedUser(row);
                setShowEditModal(true);
              }}
            />}

            {isDelete && <FontAwesomeIcon
              icon={faTrash}
              size="lg"
              className="text-danger"
              title="Delete"
              style={{ cursor: "pointer" }}
              onClick={() => {
                setSelectedUser(row);
                setShowDeleteModal(true);
              }}
            />}
          </div>
        ),
        ignoreRowClick: true,
        allowOverflow: true,
        button: true,
      };

      baseColumns.push(actionColumn);
    }

    return baseColumns;
  }, [canManageUsers]);

  // if (!canViewUsers) {
  //   return (
  //     <div className="container mt-4">
  //       <h5 className="fw-bold mb-3">Manage Users</h5>
  //       <div className="alert alert-warning">
  //         You do not have permission to view users.
  //       </div>
  //     </div>
  //   );
  // }

  return (
    <div className={isAccountLocation ? 'shadow rounded border-0 p-3' : ''}>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className={!isAccountLocation ? "fw-bold mb-0" : "mb-0"}
          style={isAccountLocation ? { fontSize: "15px", fontWeight: 650, paddingLeft: "12px" } : {}}
        > Manage Users </h5>
        {!isSuperAdmin && !props.setActiveView && (<button className="btn btn-sm btn-primary" onClick={clickManageRole}>
          <FontAwesomeIcon icon={faUserGear} className="me-2" />
          Go to Manage Roles
        </button>)}
      </div>
      <div style={{ padding: isAccountLocation ? "7px" : "0px" }}>
        <div className={`card ${!isAccountLocation ? "shadow-sm  border-0" : ""}`} style={{
          boxShadow: isAccountLocation ? "0 -0.125rem 0.5rem rgba(0, 0, 0, 0.075)" : undefined,
          border: isAccountLocation ? "1px solid #ddd" : undefined, borderRadius: isAccountLocation ? "5px" : undefined,
        }}>
          <div className="card-header bg-white d-flex flex-wrap justify-content-between align-items-center gap-2">
            <input
              type="text"
              className={`form-control ${canManageUsers ? "w-25" : "w-auto flex-grow-1"}`}
              placeholder="Search by name or email"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <div className="d-flex flex-wrap gap-2">
              {/* {!isSuperAdmin && (
                <button
                  className="btn btn-sm btn-primary"
                  onClick={clickManageRole}
                >
                  <FontAwesomeIcon icon={faUserGear } className="me-2" />
                  Manage Role
                </button>
              )} */}
              {(isWrite && canManageUsers) && (
                <button
                  className="btn btn-sm btn-primary"
                  onClick={() => setShowAddModal(true)}
                >
                  <FontAwesomeIcon icon={faPlus} className="me-2" />
                  Add New User
                </button>
              )} </div>
          </div>

          <div className="card-body p-0" style={{ maxHeight: "500px", overflowY: "auto", overflowX: "auto" }}>
            <TableLoadingShell loading={isFetching}>
            <DataTable
              columns={columns}
              data={userList}
              progressPending={false}
              pagination
              paginationServer
              paginationTotalRows={totalRows}
              paginationDefaultPage={currentPage}
              onChangePage={(page) => setCurrentPage(page)}
              paginationRowsPerPageOptions={[10, 15, 20, 25, 30]}
              onChangeRowsPerPage={(size) => {
                setPerPage(size);
                setCurrentPage(1);
              }}
              highlightOnHover
              striped
              persistTableHead
              customStyles={{
                table: { style: { minHeight: "55vh" } },
                headRow: { style: { backgroundColor: "#fff", borderBottom: "1px solid #e5e7eb" } },
                rows: { style: { minHeight: "48px", borderBottom: "1px solid #f3f4f6" } },
                headCells: { style: { fontWeight: "bold", fontSize: "14px" } },
              }}
            />
            </TableLoadingShell>
          </div>
        </div>
      </div>

      {canManageUsers && (
        <>
          <AddUserModal
            show={showAddModal}
            handleClose={() => setShowAddModal(false)}
            fetchUserData={fetchUserData}
            userList={userList}
          />

          <EditUserModal
            show={showEditModal}
            handleClose={() => setShowEditModal(false)}
            user={selectedUser}
            fetchUserData={fetchUserData}
          />

          <DeleteUserModal
            show={showDeleteModal}
            handleClose={() => setShowDeleteModal(false)}
            user={selectedUser}
            fetchUserData={fetchUserData}
          />
        </>
      )}
    </div>
  );
};

export default AddManageUserFunctionality;
